import {Component, Input, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import * as uuid from 'uuid';
import {MatSnackBar} from '@angular/material/snack-bar';
import { ThingSequenceInterface } from '@app/app/shared/interfaces/thing/thing-sequence.interface';
import { ParkInterface } from '@app/app/shared/interfaces/park.interface';
import {CommandsService} from '@app/app/shared/services/commands.service';
import { UpdatedSequenceInterface } from './feature-item/custom-sequence-feature-item.component';
import { CustomSequenceTracksComponent } from './tracks/custom-sequence-tracks.component';
import { SuccessSnackbarComponent } from '@app/app/shared/snackbars/snackbar-success/snackbar-success.component';
import { WarningSnackbarComponent } from '@app/app/shared/snackbars/snackbar-warning/snackbar-warning.component';

export interface SequenceItem extends ThingSequenceInterface {
  sequenceId: string;
}

interface ISequence extends ThingSequenceInterface {
  id: string;
}
@Component({
  selector: 'park-custom-sequences',
  templateUrl: './park-custom-sequences.html',
  styleUrls: ['./park-custom-sequences.css']
})

export class ParkCustomSequencesComponent implements OnInit, OnDestroy {
  @Input() public park: ParkInterface;
  @Input() public sequences: ThingSequenceInterface[];
  @Input() public sequenceList: string[];

  @ViewChildren(CustomSequenceTracksComponent) tracks: QueryList<CustomSequenceTracksComponent>;

  objectKeys = Object.keys;
  private interval;
  inProgress = false;

  public sortedSequences: SequenceItem[] = [];
  public customSequences: SequenceItem[] = [];
  public prevState: SequenceItem[] = [];

  public selectedSequence: SequenceItem = null;
 
  constructor(private comm: CommandsService, private _snackBar: MatSnackBar) {
  }

  openSnackbar(comp: any, data: string) {
    this._snackBar.openFromComponent(comp, {
      data,
      duration: 2000,
      horizontalPosition: "center",
      verticalPosition: "top",
    });
  }

  addSequence(sequence: SequenceItem) { 
    this.inProgress = true;

    setTimeout(() => {
      this.customSequences.push(sequence);
      this.sortedSequences.push(sequence);

      this.onChangeSequence(this.sortedSequences[this.sortedSequences.length - 1]);

      this.inProgress = false;
    }, 0);
  }

  onChangeSequence(sequence: SequenceItem) {
    this.selectedSequence = sequence;
  }

  public updateSequence({featureId, track, segment, block, value}: UpdatedSequenceInterface) {
    if (this.selectedSequence[featureId]) {
      let isEdited = false;

      Object.values(this.selectedSequence[featureId].on).forEach((item, index) => {
        if (item['0'] === track && item['1'] === segment && item['2'] === block) {
          if (value && typeof value === 'number') {
            this.selectedSequence[featureId].on[index]['3'] = value;
            isEdited = true;
          } else {
            delete this.selectedSequence[featureId].on[index];
            isEdited = true;

          if (Object.keys(this.selectedSequence[featureId].on).length === 0) delete this.selectedSequence[featureId]
          }
        }
      })

      if (value && !isEdited) {
        const index = Object.keys(this.selectedSequence[featureId]['on']).length;

        this.selectedSequence[featureId]['on'][index] = {
          0: track,
          1: segment,
          2: block,
        }

        if (typeof value === 'number') this.selectedSequence[featureId]['on'][index][3] = value
      }
    } else if (value) {
      this.selectedSequence[featureId] = {
        on: {
          0: {
            0: track,
            1: segment,
            2: block,
          }
        }
      }

      if (typeof value === 'number') this.selectedSequence[featureId]['on'][0][3] = value
    } 
    
    const customIndex = this.customSequences.findIndex(sequence => sequence.sequenceId === this.selectedSequence.sequenceId && sequence.nm === this.selectedSequence.nm)

    if (customIndex > (-1)) {
      this.customSequences[customIndex] = {...this.selectedSequence};
    }

    const sequenceIndex = this.sortedSequences.findIndex(sequence => sequence.sequenceId === this.selectedSequence.sequenceId && sequence.nm === this.selectedSequence.nm)

    if (sequenceIndex > (-1)) {
      this.sortedSequences[sequenceIndex] = {...this.selectedSequence};
      this.onChangeSequence(this.sortedSequences[sequenceIndex]);
    }
  }

  public clear() {
    this.inProgress = true;

    const defaultState = this.prevState.find(sequence => sequence.sequenceId === this.selectedSequence.sequenceId && sequence.nm === this.selectedSequence.nm);
    const sequenceIndex = this.sortedSequences.findIndex(sequence => sequence.sequenceId === this.selectedSequence.sequenceId && sequence.nm === this.selectedSequence.nm);
    
    setTimeout(() => {
      if (sequenceIndex > (-1)) {
        if (defaultState) {
          this.sortedSequences[sequenceIndex] = {...defaultState};
        } else {
          this.sortedSequences[sequenceIndex] = {
            sequenceId: this.sortedSequences[sequenceIndex].sequenceId,
            nm: this.sortedSequences[sequenceIndex].nm
          };
        }

        this.onChangeSequence(this.sortedSequences[sequenceIndex]);
      }

      this.inProgress = false;
    }, 0);
  }

  public reloadPark() {
    return this.comm.getShadow(this.park.attributes.parkId, this.park.id);
  }

  public onSubmit() {
    this.inProgress = true;
    const {sequenceId, nm, ...features} = JSON.parse(JSON.stringify(this.selectedSequence));

    Object.keys(features).forEach(featureId => {
      features[featureId] = {
        on: Object.values(features[featureId].on).map(track => Object.values(track))}
    });
    
    const params: ISequence = {
      id: sequenceId || uuid.v4(),
      nm,
      ...features,
    }

    if (sequenceId) {
      this.comm.editSequence(this.park.attributes.parkId, this.park.id, params).then((res) => {
        this.reloadPark();

        this.inProgress = false;
        this.openSnackbar(SuccessSnackbarComponent, "Successfully updated");
      }).catch((err) => {
        const text = this.generateError(err);
        this.inProgress = false;
        this.openSnackbar(WarningSnackbarComponent, text);
      });
    } else {
      this.comm.createSequence(this.park.attributes.parkId, this.park.id, params)
        .then((res) => {
          this.reloadPark();
          this.sortSequences();

          this.inProgress = false;
          this.openSnackbar(SuccessSnackbarComponent, "Successfully added");
        })
        .catch((err) => {
          const text = this.generateError(err);
          this.inProgress = false;
          this.openSnackbar(WarningSnackbarComponent, text);
        });
    }
  }

  public deleteSequence() {
    this.inProgress = true;

    if (this.selectedSequence.sequenceId) {
      const params = {
        id: this.selectedSequence.sequenceId,
        nm: this.selectedSequence.nm,
      };
  
      this.comm.deleteSequence(this.park.attributes.parkId, this.park.id, params).then((res) => {
        this.reloadPark();
  
        this.inProgress = false;
        this.openSnackbar(SuccessSnackbarComponent, "Successfully deleted");
        this.selectedSequence = null;
        this.sortSequences();
      }).catch((err) => {
        const text = this.generateError(err);
        this.inProgress = false;
        this.openSnackbar(WarningSnackbarComponent, text);
      });
    } else {
      const customIndex = this.customSequences.findIndex(sequence => sequence.nm === this.selectedSequence.nm)

      if (customIndex > (-1)) {
        this.customSequences.splice(customIndex, 1);
        this.selectedSequence = null;
        this.sortSequences();
        this.openSnackbar(SuccessSnackbarComponent, "Successfully deleted");
      }
      this.inProgress = false;
    }
  }

  private generateError(code: number):string {
    let errText: string = "";

    switch (code) {
      case -32002:
        errText = "Overlap error"
        break;
      case -32003:
        errText = "Invalid time"
        break;
      case -32600:
        errText = "Invalid request"
        break;
      case -32602:
        errText = "Invalid params"
        break;
    }

    return "Internal Server Error" + (errText ? ` (${errText})` : "");
  }

  public sortSequences() {
    const sortedSequences = [];

    Object.keys(this.sequences).forEach((sequenceId) => {
      if (Object.values(this.sequenceList).indexOf(this.sequences[sequenceId].nm) > -1) {
        sortedSequences.push({ sequenceId, ...this.sequences[sequenceId], nm: this.sequences[sequenceId].nm || "" });
      }
    });

    this.prevState = JSON.parse(JSON.stringify(sortedSequences));

    if (this.customSequences.length > 0) sortedSequences.push(...this.customSequences);

    let sequenceIndex = -1;

    if (this.selectedSequence) {
      sequenceIndex = sortedSequences.findIndex(sequence => sequence.sequenceId === this.selectedSequence.sequenceId && sequence.nm === this.selectedSequence.nm)
      
      if (sequenceIndex > (-1)) {
        sortedSequences[sequenceIndex] = this.selectedSequence;
      }
    }
    
    this.sortedSequences = [...sortedSequences];

    if (this.selectedSequence && !this.selectedSequence['sequenceId']) {
      const sequenceIndex = this.sortedSequences.findIndex(sequence => sequence['sequenceId'] && sequence.nm === this.selectedSequence.nm);

      if (sequenceIndex > (-1)) {
        this.onChangeSequence(this.sortedSequences[sequenceIndex]);
        let customSequenceIndex = this.customSequences.findIndex(sequence => sequence.nm === this.selectedSequence.nm);
  
        if (customSequenceIndex > (-1)) {
          this.customSequences.splice(customSequenceIndex, 1);
        }
      }
    }

    if (this.selectedSequence) {
      const sequenceIndex = this.sortedSequences.findIndex(sequence => sequence.nm === this.selectedSequence.nm);

      if (sequenceIndex < 0) this.onChangeSequence(this.sortedSequences[0]);
    } else { this.onChangeSequence(this.sortedSequences[0]); }
  }

  ngOnInit(): void {
    this.sortSequences();
    this.onChangeSequence(this.sortedSequences[0]);

    this.interval = setInterval(() => {
      this.sortSequences();
    }, 6000);
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/dashboard/parks/park/custom-sequences/park-custom-sequences.component.ts